/**
 * CHAT NỘI BỘ — real 1-1 messages on the notifications backbone.
 *
 * Transport (routes/notifications.ts):
 *   GET  /messages?withUserId=…   both directions of one thread
 *   POST /messages                { toUserId, body }
 *
 * FRESHNESS: the open thread polls every 5 s (`useMessages`) and refetches
 * INSTANTLY when any BranchHub event arrives (`subscribeRealtime`) — messages
 * have no dedicated hub topic, so a task/alert event doubles as a cheap "the
 * world moved" nudge while the poll remains the guarantee.
 *
 * WHO CAN I MESSAGE — an honest seam:
 *   - Anyone who has messaged or notified me: the generic inbox
 *     (GET /notifications) carries `fromUserId`, a real USER id, so replying
 *     always works.
 *   - The colleague picker (GET /staff/colleagues) returns EMPLOYEE ids, and
 *     the messages API addresses USER ids — the server does not expose the
 *     mapping. The picker is wired anyway (the two ids coincide in some
 *     deployments); when the server answers 404 the screen explains exactly
 *     which field the API needs to add instead of failing mutely.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { errorMessage, queryKeys, useColleagues, useInbox, useMessages } from '../lib/queries';
import { subscribeRealtime } from '../lib/realtime';
import { useAuth } from '../auth/AuthProvider';
import { formatTimeDate } from '../lib/format';
import { Banner, Button, Card, EmptyState, ErrorState, LoadingState, SectionHeader } from '../components/ui';
import { ScreenHeader } from '../components/ScreenHeader';
import { useToast } from '../components/Toast';
import { useSyncState } from '../lib/hooks';

interface ChatPartner {
  /** What we address messages to. A user id when known-good, else employee id. */
  readonly id: string;
  readonly name: string;
  /** True when the id came from a received message (a verified USER id). */
  readonly verified: boolean;
}

export function ChatScreen(): JSX.Element {
  const [partner, setPartner] = useState<ChatPartner | null>(null);

  return partner === null ? (
    <PartnerList onPick={setPartner} />
  ) : (
    <Thread partner={partner} onBack={() => setPartner(null)} />
  );
}

// ---------------------------------------------------------------------------
// Partner list
// ---------------------------------------------------------------------------

function PartnerList({ onPick }: { readonly onPick: (partner: ChatPartner) => void }): JSX.Element {
  const inbox = useInbox();
  const colleagues = useColleagues();
  const { me } = useAuth();

  // Conversations = distinct senders of DIRECT_MESSAGE deliveries to me.
  const conversations = useMemo(() => {
    const byUser = new Map<string, { latestBody: string | null; latestAt: number; unread: number }>();
    for (const notification of inbox.data?.notifications ?? []) {
      if (notification.type !== 'DIRECT_MESSAGE') continue;
      const from = notification.fromUserId;
      if (from === null || from === me?.user.id) continue;
      const existing = byUser.get(from);
      const unread = notification.readAt === null ? 1 : 0;
      if (existing === undefined) {
        byUser.set(from, { latestBody: notification.body, latestAt: notification.createdAt, unread });
      } else {
        existing.unread += unread;
        if (notification.createdAt > existing.latestAt) {
          existing.latestAt = notification.createdAt;
          existing.latestBody = notification.body;
        }
      }
    }
    return [...byUser.entries()]
      .map(([userId, info]) => ({ userId, ...info }))
      .sort((a, b) => b.latestAt - a.latestAt);
  }, [inbox.data, me]);

  // One row per colleague (they appear once per assignment; dedupe by id).
  const uniqueColleagues = useMemo(() => {
    const seen = new Map<string, { readonly employeeId: string; readonly name: string; readonly roleCode: string | null }>();
    for (const colleague of colleagues.data?.colleagues ?? []) {
      if (!seen.has(colleague.employeeId)) {
        seen.set(colleague.employeeId, {
          employeeId: colleague.employeeId,
          name: colleague.name,
          roleCode: colleague.roleCode,
        });
      }
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name, 'vi'));
  }, [colleagues.data]);

  return (
    <div className="pb-8">
      <ScreenHeader title="Chat nội bộ" subtitle="Tin nhắn 1-1 với đồng nghiệp" />

      <SectionHeader title="Cuộc trò chuyện" />
      {inbox.isLoading ? (
        <LoadingState />
      ) : conversations.length === 0 ? (
        <EmptyState
          title="Chưa có cuộc trò chuyện nào"
          description="Khi đồng nghiệp hoặc quản lý nhắn cho bạn, cuộc trò chuyện sẽ hiện ở đây."
        />
      ) : (
        <ul className="space-y-2">
          {conversations.map((conversation) => (
            <li key={conversation.userId}>
              <Card onClick={() => onPick({ id: conversation.userId, name: 'Đồng nghiệp', verified: true })}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900">
                      Đồng nghiệp <span className="font-mono text-xs text-slate-400">{conversation.userId.slice(-6)}</span>
                    </p>
                    <p className="truncate text-sm text-slate-600">{conversation.latestBody ?? '…'}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-xs text-slate-400">{formatTimeDate(conversation.latestAt)}</p>
                    {conversation.unread > 0 ? (
                      <span className="mt-1 inline-block rounded-full bg-rose-600 px-2 py-0.5 text-xs font-bold text-white">
                        {conversation.unread}
                      </span>
                    ) : null}
                  </div>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <SectionHeader title="Bắt đầu trò chuyện" />
      {colleagues.isLoading ? (
        <LoadingState label="Đang tải danh sách đồng nghiệp…" />
      ) : colleagues.isError ? (
        <ErrorState message={errorMessage(colleagues.error)} onRetry={() => void colleagues.refetch()} />
      ) : uniqueColleagues.length === 0 ? (
        <EmptyState title="Không có đồng nghiệp nào đang có ca quanh thời gian này" />
      ) : (
        <ul className="space-y-2">
          {uniqueColleagues.map((colleague) => (
            <li key={colleague.employeeId}>
              <Card onClick={() => onPick({ id: colleague.employeeId, name: colleague.name, verified: false })}>
                <p className="font-bold text-slate-900">{colleague.name}</p>
                {colleague.roleCode === null ? null : (
                  <p className="text-sm text-slate-500">{colleague.roleCode}</p>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thread
// ---------------------------------------------------------------------------

function Thread({
  partner,
  onBack,
}: {
  readonly partner: ChatPartner;
  readonly onBack: () => void;
}): JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { online } = useSyncState();
  const thread = useMessages(partner.id);
  const [draft, setDraft] = useState('');
  const [recipientUnknown, setRecipientUnknown] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Instant refresh on any realtime event — see the module header.
  useEffect(
    () =>
      subscribeRealtime(() => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.messages(partner.id) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.inbox });
      }),
    [partner.id, queryClient],
  );

  // Newest message into view whenever the thread grows.
  const messageCount = thread.data?.messages.length ?? 0;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messageCount]);

  const send = useMutation({
    mutationFn: (body: string) => api.sendMessage(partner.id, body),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages(partner.id) });
    },
    onError: (error) => {
      // 404 on an unverified partner = the employee-id/user-id seam (header).
      if (!partner.verified && error instanceof ApiError && error.status === 404) {
        setRecipientUnknown(true);
        return;
      }
      toast.error('Không gửi được tin nhắn', errorMessage(error));
    },
  });

  const submit = (): void => {
    const body = draft.trim();
    if (body === '' || send.isPending) return;
    send.mutate(body);
  };

  // Server returns newest-first; the screen renders oldest-first.
  const messages = useMemo(() => [...(thread.data?.messages ?? [])].reverse(), [thread.data]);

  return (
    <div className="flex h-full flex-col pb-2">
      <ScreenHeader title={partner.name} subtitle="Tin nhắn được lưu trên máy chủ của nhà hàng" action={
        <button type="button" onClick={onBack} className="tap text-sm font-semibold text-brand-700">
          Danh sách
        </button>
      } />

      {recipientUnknown ? (
        <Banner tone="warning" title="Máy chủ không tìm thấy người nhận này">
          Danh bạ đồng nghiệp trả về mã nhân viên, còn tin nhắn được gửi theo mã tài khoản — API cần bổ sung{' '}
          <code className="rounded bg-amber-100 px-1 font-mono text-xs">userId</code> vào{' '}
          <code className="rounded bg-amber-100 px-1 font-mono text-xs">GET /staff/colleagues</code>. Bạn vẫn
          trả lời được bất kỳ ai đã nhắn cho bạn trước.
        </Banner>
      ) : null}

      {thread.isLoading ? (
        <LoadingState label="Đang tải tin nhắn…" />
      ) : thread.isError ? (
        <ErrorState message={errorMessage(thread.error)} onRetry={() => void thread.refetch()} />
      ) : (
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto py-2">
          {messages.length === 0 ? (
            <EmptyState title="Chưa có tin nhắn nào" description="Nhắn câu đầu tiên ở ô bên dưới." />
          ) : (
            messages.map((message) => (
              <div key={message.id} className={message.mine ? 'flex justify-end' : 'flex justify-start'}>
                <div
                  className={[
                    'max-w-[80%] rounded-2xl px-3 py-2',
                    message.mine ? 'rounded-br-md bg-brand-600 text-white' : 'rounded-bl-md bg-white text-slate-900 shadow-sm',
                  ].join(' ')}
                >
                  <p className="whitespace-pre-wrap break-words text-sm">{message.body ?? ''}</p>
                  <p className={`mt-1 text-right text-[10px] ${message.mine ? 'text-brand-100' : 'text-slate-400'}`}>
                    {formatTimeDate(message.createdAt)}
                    {message.mine && message.readAt !== null ? ' · đã xem' : ''}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Composer pinned to the bottom third — thumb territory. */}
      <div className="sticky bottom-0 mt-2 flex items-end gap-2 rounded-2xl bg-white p-2 shadow-sm">
        <textarea
          rows={1}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={online ? 'Nhắn tin…' : 'Đang ngoại tuyến — cần mạng để nhắn tin'}
          disabled={!online || recipientUnknown}
          className="field max-h-28 min-h-touch flex-1 resize-none"
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
        />
        <Button
          size="lg"
          onClick={submit}
          loading={send.isPending}
          disabled={!online || recipientUnknown || draft.trim() === ''}
          ariaLabel="Gửi tin nhắn"
        >
          Gửi
        </Button>
      </div>
    </div>
  );
}
